// src/components/Category/VerticalContentCard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { convertToPersianNumbers } from '../../utils/PersianNumberUtils';

const SAFE_FALLBACK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const htmlToPlainText = (html) => {
  if (!html) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const TAILWIND_GRADIENTS = [
  'from-red-500', 'from-blue-500', 'from-green-500', 'from-purple-500',
  'from-pink-500', 'from-indigo-500', 'from-yellow-500', 'from-orange-500',
  'from-rose-500', 'from-violet-500', 'from-fuchsia-500', 'from-sky-500',
  'from-emerald-500', 'from-amber-500'
];

const RandomFallbackImage = ({ src: initialSrc, alt, className = '' }) => {
  const [displayUrl, setDisplayUrl] = useState(initialSrc || SAFE_FALLBACK);
  const [hasAttemptedFallback, setHasAttemptedFallback] = useState(!initialSrc);
  const randomGradientClass = useMemo(() => {
    const color = TAILWIND_GRADIENTS[Math.floor(Math.random() * TAILWIND_GRADIENTS.length)];
    return `absolute inset-0 bg-gradient-to-r ${color} to-transparent opacity-30 pointer-events-none`;
  }, []);

  useEffect(() => {
    if (initialSrc) {
      setDisplayUrl(initialSrc);
      setHasAttemptedFallback(false);
    }
  }, [initialSrc]);

  const handleImageError = useCallback(() => {
    if (!hasAttemptedFallback && initialSrc) {
      setDisplayUrl(SAFE_FALLBACK);
      setHasAttemptedFallback(true);
    }
  }, [hasAttemptedFallback, initialSrc]);

  return (
    <div className="relative w-full h-full overflow-hidden rounded-lg">
      <img
        key={displayUrl}
        src={displayUrl}
        alt={alt || 'تصویر محصول'}
        className={`w-full h-full object-cover transition duration-200 group-hover:scale-[1.03] ${className}`}
        style={{ filter: 'grayscale(100%) contrast(150%)' }}
        onError={handleImageError}
        loading="lazy"
        aria-hidden={!alt}
      />
      <div className={randomGradientClass} />
      <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </div>
  );
};

const VerticalContentCard = ({ data }) => {
  if (!data) return null;

  const mainData = data.product_details || data.product || data;

  const {
    slug = '',
    title = 'بدون عنوان',
    description = '',
    price = null,
    price_display = '',
    is_free = false,
    cover_url = null,
    course_type_name = '',
    product_url = null,
    category_slug = '',
    subcategory_slug = '',
  } = mainData;

  const plainDescription = htmlToPlainText(description);
  const finalDescription = plainDescription?.trim() || 'توضیحات کوتاه موجود نیست.';
  const descriptionClass = plainDescription?.trim() ? 'text-gray-600' : 'text-gray-400 italic';

  const finalProductLink = product_url || (slug
    ? (category_slug
        ? (subcategory_slug
            ? `/products/${category_slug}/${subcategory_slug}/${slug}`
            : `/products/${category_slug}/${slug}`)
        : `/products/${slug}`)
    : '#');

  let displayPrice = 'تماس بگیرید';
  const isFreeContent = is_free === true || (price !== null && price <= 0);
  if (isFreeContent) {
    displayPrice = 'رایگان';
  } else if (price_display?.trim()) {
    displayPrice = convertToPersianNumbers(price_display);
  } else if (price !== null && price !== undefined) {
    displayPrice = convertToPersianNumbers(price);
  }

  const priceLabelBgClass = isFreeContent ? 'bg-cyan-700' : 'bg-indigo-700';
  const shouldShowPriceLabel = isFreeContent || price !== null || (price_display && price_display.trim());

  return (
    <Link
      to={finalProductLink}
      className="w-full relative block rtl text-right p-3 border border-gray-200 rounded-tr-lg rounded-bl-lg rounded-tl-sm rounded-br-sm hover:shadow-md transition duration-200 group"
      aria-label={`مشاهده جزئیات ${title}`}
    >
      <div className="flex flex-row space-x-reverse space-x-3">
        <div className="relative flex-shrink-0 w-[100px] aspect-square">
          <RandomFallbackImage
            src={cover_url}
            alt={title}
            className="w-full h-full object-cover rounded-lg transition duration-200 group-hover:scale-[1.03]"
          />
          {shouldShowPriceLabel && (
            <span
              className={`absolute bottom-0 left-0 ${priceLabelBgClass} text-white text-[10px] font-medium px-3 py-1 shadow-md rounded-tr-lg rounded-bl-lg z-10`}
            >
              {displayPrice}
            </span>
          )}
        </div>

        <div className="flex flex-col flex-grow min-w-0 md:w-[250px] py-1">
          <h3 className="text-[11px] font-bold text-gray-800 mb-1 leading-6 line-clamp-2 group-hover:text-indigo-700 transition">
            {title}
          </h3>
          {course_type_name && (
            <span className="text-xs font-medium text-indigo-600 mb-2">
              {course_type_name}
            </span>
          )}
          <p className={`text-xs font-light line-clamp-3 ${descriptionClass}`}>
            {finalDescription}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default VerticalContentCard;